import { isJestRuntime } from './build-env';
import { getRuntime, runtimeKeys } from '@codametrix/client-runtime';
/**
 * Log level constants used to control logging verbosity.
 * Lower numbers are more verbose, higher numbers are more restrictive.
 */
export const LOG_LEVELS = {
  /** Detailed diagnostic information, typically only of interest when diagnosing problems */
  DEBUG: 1,
  /** General information about application flow and state changes */
  INFO: 2,
  /** Potentially harmful situations or deprecated usage */
  WARN: 3,
  /** Error events that might still allow the application to continue running */
  ERROR: 4,
  /** Completely disable logging output */
  NO_LOG: 100
};

const getLogLevel = () => {
  const runtimeLogLevel = getRuntime().value(runtimeKeys.UI_LOG_LEVEL);
  if (runtimeLogLevel !== '') {
    return LOG_LEVELS[runtimeLogLevel.toUpperCase() as keyof typeof LOG_LEVELS] || LOG_LEVELS.WARN;
  }
  return LOG_LEVELS.WARN;
};

const doLog = (requestedLevel) => {
  if (isJestRuntime()) {
    return false;
  }
  const currentLevel = getLogLevel();
  return requestedLevel >= currentLevel;
};

const timestamp = () =>
  new Date().toISOString().replace('T', ' ').replace('Z', '');

// Cache for storing prefixed loggers
const loggerCache = new Map<string, typeof logger>();

/**
  * logging prototype
  */
const logger = {
  error: (...args) => {
    args.unshift(timestamp());
    doLog(LOG_LEVELS.ERROR) && console.error.apply(null, args);
  },
  warn: (...args) => {
    args.unshift(timestamp());
    doLog(LOG_LEVELS.WARN) && console.warn.apply(null, args);
  },
  debug: (...args) => {
    args.unshift(timestamp());
    doLog(LOG_LEVELS.DEBUG) && console.log.apply(null, args);
  },
  log: (...args) => {
    args.unshift(timestamp());
    doLog(LOG_LEVELS.INFO) && console.log.apply(null, args);
  }
};


/**
 * Creates a prefixed logger instance that prepends a component/module identifier to all log messages.
 * The returned logger maintains the same interface as the base logger but adds the prefix for better
 * log organization and filtering.
 * 
 * @param prefix - The string identifier to prepend to all log messages (will be wrapped in brackets)
 * @returns A logger instance with the same methods as the base logger but with prefixed output
 * 
 * @example
 * 
 * // Create a logger for a specific component
 * const componentLogger = loggerFactory('DataGrid');
 * 
 * // Use it like the regular logger
 * componentLogger.log('Component initialized');
 * // Output: "2023-12-07 10:30:45.123 [DataGrid] Component initialized"
 * 
 * componentLogger.error('Failed to load data', error);
 * // Output: "2023-12-07 10:30:45.456 [DataGrid] Failed to load data Error: ..."
 * 
 * // Create loggers for different modules
 * const apiLogger = loggerFactory('API');
 * const uiLogger = loggerFactory('UI');
 * 
 * apiLogger.debug('Making request to /users');
 * uiLogger.warn('Deprecated prop usage detected');
 * 
 */
export const loggerFactory = (prefix: string) => {
  // Check if we already have a logger for this prefix
  if (loggerCache.has(prefix)) {
    return loggerCache.get(prefix)!;
  }

  // Create a new logger that prepends the string to all the log statements
  const prefixedLogger = Object.create(logger);
  
  Object.keys(logger).forEach(method => {
    prefixedLogger[method] = (...args) => logger[method](`[${prefix}]`, ...args);
  });
  
  // Store in cache before returning
  loggerCache.set(prefix, prefixedLogger);
  
  return prefixedLogger;
};
